import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Bell, AlertTriangle, BarChart3, CreditCard, MapPin, UserCog, HelpCircle,
  FileText, LogOut, ChevronRight, ClipboardList
} from 'lucide-react';

/**
 * Manager "More" — Phase 1 UX consolidation.
 * Every item here is a link to an already-existing page. No routes or
 * business logic were changed; only the primary navigation was flattened
 * from 13 items down to 5, with these advanced/less-frequent destinations
 * grouped here.
 */
export default function ManagerMorePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Sections mirror the Phase 1 spec exactly:
  //  Updates · Reports/Analytics · Billing · Branches · Profile · Help ·
  //  Legal · Sign out.
  const sections = [
    {
      title: 'Attention',
      items: [
        { to: '/manager/notifications', icon: Bell, label: 'Updates',        hint: 'Recent messages, alerts and system updates' },
        { to: '/manager/alerts',        icon: AlertTriangle, label: 'Needs Attention', hint: 'Discrepancy checks awaiting your review' },
      ],
    },
    {
      title: 'Business',
      items: [
        { to: '/manager/analytics',     icon: BarChart3,     label: 'Reports & Analytics', hint: 'Trends, costs, worker performance' },
        { to: '/manager/shift-records', icon: ClipboardList, label: 'Past Shifts',         hint: 'Completed shift audit trail' },
        { to: '/manager/billing',       icon: CreditCard,    label: 'Billing',             hint: 'Subscription and payment method' },
        { to: '/manager/branches',      icon: MapPin,        label: 'Branches',            hint: 'Saved branch locations' },
      ],
    },
    {
      title: 'Your account',
      items: [
        { to: '/manager/profile', icon: UserCog,     label: 'Profile',       hint: 'Your details, plan, saved branches' },
        { to: '/manager/settings', icon: Bell,       label: 'Settings',      hint: 'Notifications, privacy, security, delete account' },
        { to: '/manager/help',    icon: HelpCircle,  label: 'Help',          hint: 'Guides, FAQs and support contact' },
        { to: '/manager/help#legal', icon: FileText, label: 'Legal',         hint: 'Terms, privacy policy, DPA' },
      ],
    },
  ];

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto pb-32 lg:pb-8" data-testid="manager-more-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 font-['Public_Sans']">More</h1>
        <p className="text-[14px] text-slate-500 mt-1">
          Signed in as <span className="font-semibold text-slate-700">{user?.fullName}</span>
        </p>
      </div>

      {sections.map((section) => (
        <div key={section.title} className="mb-6" data-testid={`more-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 px-1 mb-2">{section.title}</p>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {section.items.map((it) => (
              <button
                key={it.to}
                type="button"
                onClick={() => navigate(it.to)}
                className="w-full flex items-center gap-4 px-4 py-4 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left min-h-[64px]"
                data-testid={`more-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="h-11 w-11 rounded-xl bg-[#4338CA]/10 flex items-center justify-center shrink-0">
                  <it.icon className="h-5 w-5 text-[#4338CA]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[16px] font-semibold text-slate-900">{it.label}</p>
                  <p className="text-[13px] text-slate-500 truncate">{it.hint}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 h-14 rounded-2xl border-2 border-slate-200 text-slate-700 font-semibold text-[16px] hover:bg-slate-50 active:bg-slate-100 transition-colors"
        data-testid="more-sign-out-btn"
      >
        <LogOut className="h-5 w-5" />
        Sign out
      </button>
    </div>
  );
}
